import { useSnackbar } from 'notistack';
import React from 'react';
import { useAccount, useNetwork, useWalletClient } from 'wagmi';
import { Formik, Form } from 'formik';
import { LoadingButton } from '@mui/lab';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useChainModal } from '@rainbow-me/rainbowkit';
import { getNonce, submitAndVerify } from '../../services/api';
import { Nonce } from '../../types';
import {
  countries,
  shippingFormSchema,
  ShippingFormValues,
} from '../../utils/form';
import { Input } from './Input';
import { Select } from './Select';

const ShippingForm: React.FC<{
  onSuccess: () => void;
}> = ({ onSuccess }) => {
  const {data: walletClient} = useWalletClient();
  const { address, isConnected } = useAccount();
  const { openChainModal } = useChainModal();
  const [error, setError] = React.useState<string | undefined>();
  const [nonce, setNonce] = React.useState<Nonce | undefined>();
  const [captchaToken, setCaptchaToken] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const { chain } = useNetwork();
  const { enqueueSnackbar } = useSnackbar();

  const getAuth = React.useCallback(async () => {
    if (isConnected && address) {
      try {
        setError(undefined);
        setLoading(true);
        if (chain?.unsupported) {
          setError('Wrong network: Please switch to mainnet');
          if (openChainModal) {
            openChainModal();
          }
          return;
        }
        const newNonce = await getNonce(address);
        setNonce(newNonce);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  }, [address, chain?.unsupported, isConnected, openChainModal]);

  const onSubmit = React.useCallback(
    async (values: ShippingFormValues) => {
      if (walletClient && nonce && address) {
        try {
          setLoading(true);
          const signature = await walletClient.signMessage({
            message: nonce.signatureRequest
          });
          await submitAndVerify(address, signature, nonce.token, values);
          onSuccess();
        } catch (err: any) {
          if (!err.message.contains('user rejected signing')) {
            enqueueSnackbar(err.message, {
              variant: 'error',
            });
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [address, enqueueSnackbar, nonce, onSuccess, walletClient]
  );

  React.useEffect(() => {
    getAuth();
  }, [getAuth]);

  return (
    <div
      className="p-accountFormOuter"
      style={{
        marginTop: '2em',
      }}>
      {!isConnected && (
        <div
          style={{
            marginTop: '2em',
            marginBottom: '2em',
          }}>
          Connect your wallet to get started
        </div>
      )}
      {error && (
        <div
          style={{
            marginTop: '2em',
            marginBottom: '2em',
          }}>
          {error}
        </div>
      )}
      {isConnected && !error && nonce && (
        <Formik
          initialValues={{
            name: '',
            // zipCode: '',
            // addressLine: '',
            // city: '',
            state: '',
            // phone: '',
            country: '日本(Japan)',
            email: '',
            // quantity: nonce.quantity,
            quantity: 1,
          }}
          validationSchema={shippingFormSchema}
          onSubmit={async (values, { setSubmitting }) => {
            await onSubmit(values);
            setSubmitting(false);
          }}>
          {({ errors, touched }) => (
            <Form className="p-accountForm">
              <Input label="Name" name="name" type="text" />
              <Input label="E-Mail" name="email" type="email" />
              {/* <Input label="Zip Code" name="zipCode" type="text" /> */}
              {/* <Input label="Address" name="addressLine" type="text" /> */}
              {/* <Input label="City/Town" name="city" type="text" /> */}
              <Select label="Country" name="country">
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Select>
              <Input label="State/Province/Region" name="state" type="text" />
              {/* <Input
                label="Phone Number"
                name="phone"
                type="text"
                placeholder="+81-3-1234-5678"
              /> */}
              <Input
                label="Pre-order Quantity of Box"
                name="quantity"
                type="number"
                min={1}
                max={12}
              />
              <div
                style={{
                  marginTop: '2em',
                }}>
                <HCaptcha
                  sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || ''}
                  onVerify={setCaptchaToken}
                />
                <LoadingButton
                  disabled={
                    !captchaToken ||
                    (errors &&
                      Object.keys(errors).length > 0 &&
                      touched &&
                      Object.keys(touched).length > 0)
                  }
                  loading={loading}
                  type="submit">
                  SUBMIT
                </LoadingButton>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default ShippingForm;
